import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FileTextIcon, GraduationCapIcon, LayersIcon, MusicIcon, PartyPopperIcon, UsersIcon, Volume2Icon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Brand name used across the landing (one place to change it).
export const BRAND = 'MUSIKK KLAR™';

// Mockup (box with 6 bind) in Impultienda Recursos (WebP, transparent background).
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/fb8e724e-7706-49dd-b9ba-b2841fdae752/1792634679269_musikkklar-mockup-estandar-caja-6-bind-transparente-a6d31710.webp';

export const heroGuarantees = ['Umiddelbar digital nedlasting', 'Engangsbetaling', 'Ingen abonnement', '7 dagers fornøydgaranti'];
export const stats = [{
  value: '240',
  label: 'Sider i hovedbindene'
}, {
  value: '6',
  label: 'Digitale bind'
}, {
  value: '10',
  label: 'Trinn – fra 1. til 10.'
}, {
  value: '5',
  label: 'Bonuser inkludert'
}, {
  value: '3',
  label: 'Nivåer på aktivitetene'
}];
export const differenceStats = [{
  value: '1.–10. trinn',
  label: 'Fra barneskole til ungdomsskole'
}, {
  value: '6 digitale bind',
  label: 'Fra sang, rytme og samspill til lage musikk, kulturforståelse og standpunkt'
}, {
  value: 'Også uten instrumenter',
  label: 'Kroppsperkusjon, stemme og lytting fungerer i alle klasserom'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Kvelder og helger forsvinner',
  text: 'Timer foran skjermen for å planlegge neste ukes musikktimer – og du er fortsatt ikke ferdig.'
}, {
  title: 'Støy og kaos med instrumentene',
  text: 'Når trommer og boomwhackers kommer fram, blir det fort bråk – og timen glipper før dere har kommet i gang.'
}, {
  title: 'Du har ikke musikkutdanning',
  text: 'Du er kontaktlærer og har fått musikk på timeplanen, men spiller ikke selv og vet ikke helt hvor du skal begynne.'
}, {
  title: 'Leting etter sanger og opplegg',
  text: 'Sanger, leker og ideer fra ulike nettsider passer ikke trinnet, kompetansemålene eller klassen din.'
}, {
  title: '17. mai, jul og sommeravslutning',
  text: 'Hvert år det samme: noe skal øves inn til fremføring, og du starter fra null igjen.'
}, {
  title: 'Vanskelig å vurdere musikk',
  text: 'Underveisvurdering og standpunkt i et praktisk fag – hvordan begrunner og dokumenterer du det?'
}];
export const afterItems = ['En ferdig årsplan for 1.–10. trinn', '12 ferdige musikktimer minutt for minutt', 'Materiell og lærerinstruks til hver time', 'Aktiviteter i 3 nivåer', 'Sangbank med 30 tradisjonelle sanger', 'Rutiner for oppstart, instrumenter og avslutning', 'Koblet til kompetansemål i LK20', 'Vurderingskriterier, standpunkt og ferdige tilbakemeldinger', 'Fungerer også uten instrumenter'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kveldene går med til planlegging',
  text: 'Bruker du kvelder og helger på å planlegge neste ukes musikktimer?'
}, {
  icon: Volume2Icon,
  title: 'Klassen blir urolig med instrumentene',
  text: 'Trenger du faste signaler og rutiner som gjør at instrumentene skaper musikk – ikke bråk?'
}, {
  icon: GraduationCapIcon,
  title: 'Du har ikke musikkfaglig bakgrunn',
  text: 'Underviser du i musikk uten å spille selv, og vil ha opplegg som er lette å følge steg for steg?'
}, {
  icon: UsersIcon,
  title: 'Klassen har store nivåforskjeller',
  text: 'Noen elever spiller allerede, andre har aldri holdt et instrument – og timen skal fungere for alle.'
}, {
  icon: PartyPopperIcon,
  title: '17. mai og juleavslutning nærmer seg',
  text: 'Vil du ha ferdige sanger og en plan for fremføring, uten å lage alt fra bunnen av?'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurderingen tar tid',
  text: 'Vil du ha ferdige vurderingskriterier, egenvurdering og tilbakemeldinger – helt fram til standpunkt i 10. trinn?'
}];

// Spine colours mirror the physical box (blue, turquoise, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Bind 01',
  level: '1.–4. trinn',
  title: 'Årsplan & 12 ferdige musikktimer',
  tagline: 'Vit nøyaktig hva hver musikktime skal handle om – hele skoleåret.',
  intro: 'Det første bindet gir deg en komplett årsplan for 1.–4. trinn og tolv ferdige musikktimer som du kan gjennomføre med en gang.',
  items: ['Årsplan for 38 skoleuker', 'Koblet til kompetansemål etter 2. og 4. trinn', '12 ferdige timer minutt for minutt (45 og 60 min)', 'Materiell og lærerinstruks til hver time', 'Puls og rytme, sang og sangleker', 'Bevegelse og dans', 'Klasseinstrumenter og boomwhackers', 'Lytting og enkel notasjon med figurer', 'Aktiviteter i 3 nivåer', 'Oppstartsrutine og avslutningsrutine'],
  outro: 'Ferdige timer som gjør at du går inn i musikkrommet med en tydelig plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 02',
  level: '1.–7. trinn',
  title: 'Sang, rytme & bevegelse',
  tagline: 'Få hele klassen til å synge, klappe og bevege seg sammen.',
  intro: 'Det andre bindet er en praktisk sangbank med rytme- og bevegelsesaktiviteter som fungerer også uten instrumenter.',
  items: ['Sangbank med 30 tradisjonelle sanger og sangleker', 'Stemmebruk og oppvarming', 'Kroppsperkusjon', 'Rytmekort', 'Sangdans og ringleker', 'Kanon', 'Progresjon i 3 nivåer'],
  outro: 'Musikkglede fra første minutt – med enkle, trygge opplegg.',
  spine: 'bg-cyan-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 03',
  level: '5.–7. trinn',
  title: 'Spill & samspill i klassen',
  tagline: 'Fra kaos til samspill – med hele klassen.',
  intro: 'Det tredje bindet gir deg ferdige opplegg for å spille sammen, med enkle partiturer og stasjoner som holder alle i gang.',
  items: ['Klasseinstrumenter og boomwhackers', 'Ukulele med 4 grep', 'Keyboard: C-dur og akkorder', 'Trommer', 'Samspill i grupper', 'Enkle partiturer med bokstaver og farger', 'Rotasjonsstasjoner', 'Aktiviteter i 3 nivåer'],
  outro: 'Tydelige rutiner som gjør instrumentene til musikk – ikke støy.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Bind 04',
  level: '5.–10. trinn',
  title: 'Lage musikk & digital musikk',
  tagline: 'La elevene skape sin egen musikk.',
  intro: 'Det fjerde bindet tar elevene fra improvisasjon og lydlandskap til låtskriving og digital musikkproduksjon.',
  items: ['Improvisasjon', 'Lydlandskap', 'Lage rytmer og melodier', 'Låtskriving steg for steg', 'Musikk til film og bilde', 'Digital musikkproduksjon i BandLab og GarageBand', 'Loop og beat', 'Prosjekter med vurderingskriterier'],
  outro: 'Kreative prosjekter som er enkle å lede – også for deg som ikke er produsent.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 05',
  level: '1.–10. trinn',
  title: 'Oppleve musikk & kulturforståelse',
  tagline: 'Lytt, snakk og forstå musikken rundt oss.',
  intro: 'Det femte bindet dekker kjerneelementet kulturforståelse med aktiv lytting, musikkhistorie og musikk fra Norge og verden.',
  items: ['Aktiv lytting', 'Sjangre', 'Musikkhistorie fra middelalder til pop', 'Norsk folkemusikk: hardingfele, slåtter og stev', 'Samisk joik', 'Edvard Grieg', 'Musikk i ulike kulturer', 'Musikk og identitet', 'Lytteark og samtalekort'],
  outro: 'Ferdige lytteøkter som gir gode samtaler i klassen.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Bind 06',
  level: '8.–10. trinn',
  title: 'Ungdomstrinnet, vurdering & standpunkt',
  tagline: 'Vis hva elevene kan – og sett standpunkt med trygghet.',
  intro: 'Det sjette bindet samler band i klasserommet og fremføring med verktøyene for vurdering og standpunkt.',
  items: ['Band i klasserommet', 'Fremføring og konsert', 'Underveisvurdering', 'Vurderingskriterier i 3 nivåer (utøve · lage · oppleve)', 'Egenvurdering', 'Standpunkt 10. trinn', '100 ferdige tilbakemeldinger'],
  outro: 'En komplett støtte for å vurdere rettferdig i et praktisk fag.',
  note: 'Materialet er selvstendig utviklet og er ikke et offisielt produkt fra Utdanningsdirektoratet.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digitale bind i musikk',
  text: 'Et komplett undervisningssystem som hjelper deg å planlegge og gjennomføre musikkfaget hele skoleåret. Hvert bind har 40 sider med ferdige opplegg, aktiviteter i 3 nivåer og tydelig lærerinstruks.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Ferdige timer minutt for minutt',
  text: 'Slipp å begynne på et tomt ark. Tydelige opplegg med tider, materiell og lærerinstruks hjelper deg å komme raskt i gang.'
}, {
  icon: LayersIcon,
  title: 'Aktiviteter i 3 nivåer',
  text: 'Samme mål i tre nivåer – støtte, basis og utfordring – slik at både nybegynnere og elever som spiller kan delta.'
}, {
  icon: MusicIcon,
  title: 'Fungerer også uten instrumenter',
  text: 'Stemme, kroppsperkusjon, bevegelse og lytting gjør at du kan ha gode musikktimer i alle klasserom.'
}, {
  icon: FileTextIcon,
  title: 'Koblet til LK20',
  text: 'Kjerneelementene musikalske praksiser – utøve, lage og oppleve – og kulturforståelse, tydelig koblet til kompetansemålene.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Vurdering & standpunkt',
  text: 'Underveisvurdering, vurderingskriterier i 3 nivåer, egenvurdering, standpunkt i 10. trinn og 100 ferdige tilbakemeldinger.'
}, {
  icon: CalendarCheckIcon,
  title: 'Årsplan og konserter',
  text: 'Hele skoleåret organisert – med plan for 17. mai, jul og sommeravslutning.'
}];
// Real pages from Bind 1 made in Gamma Studio (media/bind1_estudio.pdf, rendered at 3.5x, WebP in Impultienda Recursos).
const PAGE_BASE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/fb8e724e-7706-49dd-b9ba-b2841fdae752/';
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: `${PAGE_BASE}1792637066184_mkl-bind1-side01-b7b99361.webp`,
  label: 'Bind 1 · Side 1',
  title: 'Årsplan & 12 ferdige musikktimer · 1.–4. trinn',
  text: 'Hvert bind starter med en tydelig oversikt over temaene og hva som er inkludert.'
}, {
  src: `${PAGE_BASE}1792637071201_mkl-bind1-side06-c589a362.webp`,
  label: 'Bind 1 · Side 6',
  title: 'Årsplan 1. trinn – 38 skoleuker',
  text: 'Hele skoleåret uke for uke, med tema og nøkkelaktivitet for høst og vår – også 17. mai og sommeravslutning.'
}, {
  src: `${PAGE_BASE}1792637076432_mkl-bind1-side14-17e8b037.webp`,
  label: 'Bind 1 · Side 14',
  title: 'Musikktime 1: Puls i kroppen',
  text: 'En ferdig time på 45 minutter: mål, materiell, minutt for minutt, lærerinstruks – og et alternativ uten instrumenter.'
}, {
  src: `${PAGE_BASE}1792637080966_mkl-bind1-side17-97bc98db.webp`,
  label: 'Bind 1 · Side 17',
  title: 'Aktivitet: Sanglekene våre',
  text: 'Samme aktivitet i tre nivåer, med tydelige regler og hva du skal se etter.'
}, {
  src: `${PAGE_BASE}1792637085055_mkl-bind1-side38-4de220b4.webp`,
  label: 'Bind 1 · Side 38',
  title: 'Vurderingskriterier i 3 nivåer',
  text: 'Utøve, lage, oppleve og kulturforståelse – på vei, når målet og går dypere.'
}];
export const pageTypes = ['Ferdige musikktimer', 'Aktiviteter i 3 nivåer', 'Sangbank', 'Vurdering'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Årsplan i musikk – ukeplaner for 1.–10. trinn',
  text: 'Ferdige ukeplaner for hele skoleåret, slik at du alltid vet hva neste musikktime skal handle om.',
  items: ['Ukeplaner for 1.–10. trinn', '38 skoleuker', 'Koblet til kompetansemål', 'Klar til utskrift'],
  benefit: 'Hele året på plass – før det har begynt.'
}, {
  title: 'Oppvarmingsbanken – 60 musikkoppvarminger på 5 minutter',
  text: 'Korte oppvarminger med stemme, rytme og bevegelse som samler klassen og setter i gang timen.',
  items: ['60 oppvarminger', 'Stemme, rytme og kropp', 'Bare 5 minutter hver', 'Ingen instrumenter nødvendig'],
  benefit: 'En rolig og fokusert start på hver time.'
}, {
  title: 'Musikkleker – 50 leker som samler hele klassen',
  text: 'Sangleker, rytmeleker og lytteleker som gjør musikktimen aktiv, morsom og inkluderende.',
  items: ['50 leker', 'Alle trinn', 'Sang, rytme og lytting', 'Klar til bruk'],
  benefit: 'Mer musikkglede – mindre mas.'
}, {
  title: 'Konsertpakken – 17. mai, jul og sommeravslutning',
  text: 'Ferdige planer for å øve inn og gjennomføre fremføringer til årets høydepunkter.',
  items: ['17. mai', 'Juleavslutning', 'Sommeravslutning', 'Øveplan uke for uke'],
  benefit: 'Trygge fremføringer uten stress i siste liten.'
}, {
  title: 'Vikarpakken – 12 ferdige musikktimer uten instrumenter',
  text: 'Tolv ferdige timer som en vikar kan gjennomføre uten forberedelse og uten instrumenter.',
  items: ['12 ferdige timer', 'Instruksjon til vikaren', 'Uten instrumenter', 'Klar til utskrift'],
  benefit: 'Ingen bortkastede timer når du er borte.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: `Åpne ${BRAND}`,
  text: 'Last ned pakken og åpne bindet som passer trinnet ditt.'
}, {
  title: 'Velg trinn og time',
  text: 'Finn riktig time, sang eller aktivitet for klassen din.'
}, {
  title: 'Forbered deg på 5 minutter',
  text: 'Les gjennom lærerinstruksen og gjør klart det du trenger – med eller uten instrumenter.'
}, {
  title: 'Start timen',
  text: 'Følg oppstartsrutinen og gå inn i musikktimen med en tydelig plan.'
}];
export const audienceItems = ['Underviser i musikk på 1.–10. trinn', 'Er kontaktlærer og har musikk på timeplanen', 'Ikke har musikkfaglig bakgrunn', 'Vil ha en ferdig årsplan koblet til LK20', 'Vil ha ro og struktur når instrumentene kommer fram', 'Vil ha aktiviteter i 3 nivåer', 'Skal forberede 17. mai, jul eller sommeravslutning', 'Vil spare kvelder og helger på planlegging'];
export const timeValueTasks = ['Lete etter sanger og opplegg på nettet', 'Planlegge musikktimene', 'Tilpasse aktiviteter til ulike nivåer', 'Øve inn til 17. mai og jul', 'Vurdere og sette standpunkt'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitale bind – 240 sider',
  detail: 'Årsplan og ferdige timer, sang og rytme, spill og samspill, lage musikk, kulturforståelse, vurdering og standpunkt'
}, {
  label: '5 bonuser uten ekstra kostnad',
  detail: 'Årsplan, Oppvarmingsbanken, Musikkleker, Konsertpakken og Vikarpakken'
}, {
  label: 'For 1.–10. trinn',
  detail: 'Ferdige timer, aktiviteter i 3 nivåer og vurderingskriterier – fungerer også uten instrumenter'
}];
export const offerIncludes = ['Umiddelbar digital nedlasting', 'PDF-filer du kan lagre', 'Klar til utskrift', 'Ingen fysisk vare sendes'];
export const PRICE_AMOUNT = '299';
export const CURRENCY = 'NOK';
export const PRICE = `${PRICE_AMOUNT} ${CURRENCY}`;
export const PRICE_BEFORE = `599 ${CURRENCY}`;

// Only add authentic reviews. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'Hvordan får jeg materialet?',
  a: `${BRAND} leveres digitalt. Etter kjøpet får du tilgang til PDF-filer som du kan laste ned og lagre. Du kan bruke materialet på PC, nettbrett eller mobil og skrive ut de sidene du trenger.`
}, {
  q: 'Er det tilpasset LK20?',
  a: `Ja. Årsplanen og timene er koblet til kjerneelementene og kompetansemålene i musikk for 1.–10. trinn. ${BRAND} er et selvstendig undervisningsmateriell og ikke et offisielt produkt fra Utdanningsdirektoratet.`
}, {
  q: 'Jeg har ikke musikkutdanning. Passer det for meg?',
  a: 'Ja. Hver time har minutt-for-minutt-plan og lærerinstruks, slik at du kan følge opplegget steg for steg – også om du ikke spiller et instrument selv.'
}, {
  q: 'Trenger vi instrumenter?',
  a: 'Nei. Mange aktiviteter bruker stemme, kroppsperkusjon, bevegelse og lytting. Har dere rytmeinstrumenter, boomwhackers, ukulele eller keyboard, finner du opplegg for dem også.'
}, {
  q: 'Hvilke trinn passer det for?',
  a: 'Materialet dekker 1.–10. trinn: Bind 1 for 1.–4. trinn, Bind 2 for 1.–7. trinn, Bind 3 for 5.–7. trinn, Bind 4 for 5.–10. trinn, Bind 5 for alle trinn og Bind 6 for 8.–10. trinn.'
}, {
  q: 'Kan jeg virkelig forberede en time på 5 minutter?',
  a: `${BRAND} er laget for å gjøre forberedelsene raskere. Du åpner et bind, velger en ferdig time og leser gjennom den på omtrent fem minutter. Tiden kan variere ut fra timen og behovet for tilpasning.`
}, {
  q: 'Hjelper det med 17. mai og juleavslutning?',
  a: 'Ja. Bonusen Konsertpakken gir deg planer for å øve inn og gjennomføre fremføringer til 17. mai, jul og sommeravslutning.'
}, {
  q: 'Er vurdering og standpunkt inkludert?',
  a: 'Ja. Bind 6 inneholder underveisvurdering, vurderingskriterier i 3 nivåer (utøve, lage og oppleve), egenvurdering, standpunkt i 10. trinn og 100 ferdige tilbakemeldinger.'
}, {
  q: 'Er det sanger med opphavsrett?',
  a: 'Sangbanken bruker tradisjonelle sanger og folkeviser i offentlig eie, i tillegg til egne sanger laget for materialet.'
}, {
  q: 'Er de fem bonusene inkludert?',
  a: `Ja. Alle fem bonusene er inkludert i pakken uten ekstra kostnad – alt for ${PRICE}.`
}, {
  q: 'Er det et abonnement?',
  a: 'Nei. Du betaler én gang og får tilgang til det digitale materialet. Ingen faste trekk.'
}, {
  q: 'Finnes det en garanti?',
  a: `Ja. ${BRAND} har 7 dagers fornøydgaranti. Hvis materialet ikke svarer til forventningene dine, kan du kontakte oss og be om å få pengene tilbake innen garantiperioden, i henhold til vilkårene.`
}];
export const footerLinks = ['Kjøpsvilkår', 'Personvern', 'Refusjon', 'Kontakt'];
